import { Button, Grid2, Paper, Typography } from "@mui/material";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GetRowIdParams,
	GridApi,
	GridReadyEvent,
	IRowNode,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { themeDXT } from "../../../../css/themeMESF";
import {
	OPERATOR_MODAL,
	type OperatorModal,
	getCreateOperatorPath,
	getEditOperatorPath,
	operatorUrl,
	useOperators,
} from "../OperatorsPage";
import type { Operator } from "../models/Operator";
import { UpsertDeleteModal } from "./UpsertDeleteModal";
import { useOperatorsOptionsFunctions } from "./customHooks/useOperatorsOptionsFunctions";
import { useTableOperators } from "./customHooks/useTableOperators";

type Props = {
	operatorId?: string;
	routeModal: OperatorModal;
};

export const TableOperators = ({ operatorId, routeModal }: Props) => {
	const navigate = useNavigate();
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<OperatorModal>(
		OPERATOR_MODAL.NONE,
	);
	const [selectedRowData, setSelectedRowData] = useState<Operator | null>(null);
	const [error, setError] = useState("");

	const {
		data: rows = [],
		isLoading,
		isError,
		error: queryError,
	} = useOperators();

	const selectOperatorNode = useCallback(
		(operator: Operator) => {
			const operatorNode = gridApi?.getRowNode(String(operator.OperatorId));

			if (operatorNode) {
				gridApi?.ensureNodeVisible(operatorNode);
				gridApi?.deselectAll();
				operatorNode.setSelected(true);
			}
		},
		[gridApi],
	);

	const handleNewOperator = useCallback(
		(shouldNavigate = true) => {
			gridApi?.deselectAll();
			setSelectedRowData(null);
			setShowModal(OPERATOR_MODAL.CREATE);

			if (shouldNavigate) {
				navigate(getCreateOperatorPath());
			}
		},
		[gridApi, navigate],
	);

	const editOperator = useCallback(
		(data: Operator, shouldNavigate = true) => {
			selectOperatorNode(data);
			setSelectedRowData(data);
			setShowModal(OPERATOR_MODAL.UPDATE);

			if (shouldNavigate) {
				navigate(getEditOperatorPath(data.OperatorId!));
			}
		},
		[navigate, selectOperatorNode],
	);

	const handleDeleteOperator = useCallback(
		(data: Operator) => {
			selectOperatorNode(data);
			setSelectedRowData(data);
			setShowModal(OPERATOR_MODAL.DELETE);
		},
		[selectOperatorNode],
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useOperatorsOptionsFunctions({
		handleNewOperator,
		editOperator,
		deleteOperator: handleDeleteOperator,
	});
	const getMenuOptionsRef = useRef(getMenuOptions);
	getMenuOptionsRef.current = getMenuOptions;

	const { columnDefs } = useTableOperators({
		editOperator,
		deleteOperator: handleDeleteOperator,
		showContextMenu,
	});

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			flex: 1,
			filter: false,
			floatingFilter: false,
			sortable: true,
			editable: false,
			resizable: false,
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const gridOptions = useMemo(
		() => ({
			theme: themeDXT,
			rowSelection: {
				mode: "singleRow" as const,
				checkboxes: false,
				enableClickSelection: true,
			},
		}),
		[],
	);

	const handleGridReady = useCallback((params: GridReadyEvent<Operator>) => {
		setGridApi(params.api);
	}, []);

	const handleRowDoubleClicked = useCallback(
		(event: RowDoubleClickedEvent<Operator>) => {
			if (!event.data) {
				return;
			}

			editOperator(event.data);
		},
		[editOperator],
	);

	const getRowId = useCallback(
		(params: GetRowIdParams<Operator>) => String(params.data.OperatorId),
		[],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data as Operator | undefined;
			const nativeEvent = (
				params as GetContextMenuItemsParams & { event?: MouseEvent }
			).event;

			if (data && nativeEvent) {
				params.api.deselectAll();
				params.node?.setSelected(true);
				showContextMenu(
					nativeEvent as unknown as React.MouseEvent,
					data,
					"operatorContext",
				);
			}

			return [];
		},
		[showContextMenu],
	);

	const onModalHide = useCallback(() => {
		gridApi?.deselectAll();
		setSelectedRowData(null);
		setShowModal(OPERATOR_MODAL.NONE);
		navigate(operatorUrl);
	}, [gridApi, navigate]);

	useEffect(() => {
		registerConfig({
			id: "operatorContext",
			getOptions: (data) => getMenuOptionsRef.current(data),
		});
	}, [registerConfig]);

	useEffect(() => {
		if (isError && queryError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (routeModal === OPERATOR_MODAL.NONE) {
			setSelectedRowData(null);
			setShowModal(OPERATOR_MODAL.NONE);
			gridApi?.deselectAll();
			return;
		}

		if (routeModal === OPERATOR_MODAL.CREATE) {
			handleNewOperator(false);
			return;
		}

		if (!operatorId) {
			return;
		}

		if (!gridApi) {
			return;
		}

		const operatorNode: IRowNode | undefined = gridApi?.getRowNode(operatorId);

		if (operatorNode) {
			gridApi?.ensureNodeVisible(operatorNode);
			gridApi?.deselectAll();
			operatorNode.setSelected(true);
			const selectedOperator = operatorNode.data as Operator;
			setSelectedRowData(selectedOperator);
			setShowModal(routeModal);
			return;
		}

		onModalHide();
	}, [
		gridApi,
		handleNewOperator,
		isLoading,
		onModalHide,
		operatorId,
		routeModal,
		rows,
	]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Operators
					</Typography>
				</Grid2>
				<Grid2
					container
					justifyContent="center"
					alignItems="center"
					sx={{ width: "100%" }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} sx={{ height: "64vh", width: "100%" }}>
							<AgGridReact
								loading={isLoading}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={handleRowDoubleClicked}
								onGridReady={handleGridReady}
								getRowId={getRowId}
								loadingOverlayComponent={CenteredLazyLoading}
								gridOptions={gridOptions}
							/>
						</Paper>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2
							size={{ md: 3, xs: 6 }}
							sx={{ display: "flex", justifyContent: "flex-end" }}
						>
							<Button
								variant="contained"
								color="primary"
								fullWidth
								onClick={() => handleNewOperator()}
							>
								NEW OPERATOR
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<UpsertDeleteModal
				modal={showModal}
				onHide={onModalHide}
				operator={selectedRowData}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
